<script setup lang="ts">
interface Props {
  chosenDate?: Record<string, unknown> | boolean;
  deliveryDays?: Record<string, unknown>[] | boolean;
  disabled?: boolean;
  width?: string;
}

const props = withDefaults(defineProps<Props>(), {
  chosenDate: false,
  deliveryDays: false,
  disabled: false,
  width: "70%",
});

const emit = defineEmits<{
  returnDate: [date: { full: string; dayOfWeek: number; date: Date }];
}>();

const showCalendar = ref(false);
const buttonRef = ref<HTMLElement | null>(null);
const calendarStyle = ref<Record<string, string>>({
  position: "fixed",
  opacity: "0",
  pointerEvents: "none",
  zIndex: "10000",
});

const displayDate = computed(() => {
  if (!props.chosenDate || typeof props.chosenDate !== "object") return "Select date";
  const cd = props.chosenDate as Record<string, unknown>;
  return cd.full ? String(cd.full) : "Select date";
});

function updateCalendarPosition() {
  if (!buttonRef.value) return;
  const rect = buttonRef.value.getBoundingClientRect();
  calendarStyle.value = {
    position: "fixed",
    top: `${rect.bottom + 4}px`,
    left: `${rect.left}px`,
    zIndex: "10000",
    opacity: "1",
    pointerEvents: "auto",
    transition: "opacity 0.15s ease",
  };
}

function toggleCalendar() {
  showCalendar.value = !showCalendar.value;
  if (showCalendar.value) {
    nextTick(() => updateCalendarPosition());
  }
}

function handleDateSelect(date: { full: string; dayOfWeek: number; date: Date }) {
  emit("returnDate", date);
  showCalendar.value = false;
}

onMounted(() => {
  window.addEventListener("scroll", updateCalendarPosition, true);
  window.addEventListener("resize", updateCalendarPosition);
});

onUnmounted(() => {
  window.removeEventListener("scroll", updateCalendarPosition, true);
  window.removeEventListener("resize", updateCalendarPosition);
});
</script>

<template>
  <div class="date-picker">
    <div
      ref="buttonRef"
      class="date-picker__button"
      :class="[showCalendar && 'selected', !chosenDate && 'error', disabled && 'disabled']"
      @click="!disabled && toggleCalendar()"
    >
      <div class="date-picker__button--icon">
        <fa
          :icon="['fal', 'calendar-days']"
          class="pink-text icon"
        />
      </div>
      <div class="date-picker__button--text">
        <p class="grey-text">
          Date
          <fa
            :icon="['fal', showCalendar ? 'chevron-up' : 'chevron-down']"
            class="small-icon"
          />
        </p>
        <p class="light-grey-text small-text">
          {{ displayDate }}
        </p>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="showCalendar"
        class="date-picker__overlay"
        @click="showCalendar = false"
      />
      <div
        v-if="showCalendar"
        class="date-picker__calendar"
        :style="calendarStyle"
      >
        <Calendar
          class="date-picker__calendar--component"
          :chosen-date="chosenDate"
          :delivery-days="deliveryDays"
          @return-date="handleDateSelect"
        />
      </div>
    </Teleport>
  </div>
</template>

<style lang="scss">
.date-picker {
  position: relative;

  &__button {
    cursor: pointer;
    border: 2px solid transparent;
    display: flex;
    justify-content: flex-start;
    border-radius: 0.4rem;
    padding: 0.5rem;
    width: max-content;
    min-width: 7rem;
    box-sizing: border-box;

    @media (max-width: $tablet) {
      width: 100%;
      min-width: 0;
      margin: 0;
    }

    &--icon {
      display: flex;
      align-items: center;
      margin-right: 0.5rem;
    }

    &--text {
      flex-direction: column;
      align-items: flex-start;

      .grey-text {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      p {
        margin: 0;
      }
    }

    &.disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  .selected {
    border: 2px solid var(--color-grey-3);
    background-color: var(--color-white-1);
  }

  .error {
    border: 2px solid var(--color-pink-1);
  }

  &__calendar {
    background: var(--color-white-1);
    border-radius: 0.4rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transition: 0.5s;
    min-width: 18rem;

    @media (max-width: $tablet) {
      left: 1rem !important;
      right: 1rem !important;
      min-width: unset !important;
      width: auto !important;
    }

    &--component {
      z-index: 10000;
      border: 2px solid var(--color-grey-3) !important;

      @media (max-width: $tablet) {
        width: 100% !important;
        left: 0;
      }
    }
  }

  &__overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
  }
}
</style>
